/** @jsxImportSource @emotion/react */
import { css, useTheme } from "@emotion/react";
import React, {useEffect, useState, useRef } from "react";
import {
  Button,
  Input,
  RadioGroup,
  RadioGroupItem,
  useToast,
} from "@coreai/component-library";
import ProjectTextField from "./ProjectTextField";
import TextFieldWithRadioDropdown from "./TextFieldWithRadioDropdown";
import FileInput from "./FileInput";
import {
  CampaignModel,
  createAnalysis,
  executeAnalysis,
} from "../../../../services/analysisService";
import { ProjectsModel } from "../../../../services/projectsService";
import {
  createAsset,
  triggerAssetDescription,
} from "../../../../services/assetsService";
import { RulesetExtItem, RulesetModel } from "../../../../services/rulesetService";
import CampaignTextField from "./CampaignTextField";
import { AssetModel } from "../../../../services/assetsService";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "LanguageContext";
import NavigationBlocker from "components/MainComponents/NavigationBlocker/NavigationBlocker";
import YoutubeUrlUpload from "./YoutubeUrlUpload";
import PageLoader from "./../../../MainComponents/Loader/Loader"

declare global {
  interface Window {
    isAnalysisDescFilled: boolean; 
  }
}

export default function FormCard() {
  const { t, language, setReRenderAnalysisList } = useLanguage();
  const CHARACTER_LIMIT = 250000;
  const theme = useTheme();
  const [isUpload, setIsUpload] = useState<string>('file');
  const navigate = useNavigate();
  const { toast } = useToast();
  const [project, setProject] = useState<ProjectsModel | null>(null);

  const [ruleSet, setRuleSet] = useState<RulesetModel | null>(null);
  const [campaign, setCampaign] = useState<CampaignModel | null>(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [textToUpload, setTextToUpload] = useState<string>("");
  const [asset, setAsset] = useState<AssetModel | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [review, setReview] = useState<string>("");
  const [isFormVal, setIsFormVal] = useState<boolean>(false);
  const [videoUrlType, setVideoUrlType] = useState<"youtube" | "invalid" | null>(null);

  const firstRenderRef = useRef(true);
  const analysesRenderRef = useRef({});
  const newAnalysisRef = useRef(false);
  const videoUrlRef = useRef("");

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [multiRuleset,setMultiRuleset] = useState<RulesetExtItem[] | null>([]);
  const [politicalRiskAssmnt, setPoliticalRiskAssmnt] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assetId, setAssetId] = useState<string | null>(null);

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newText = event.target.value;
    newText = newText?.replace(/^\s+/, "");
    newText = newText?.replace(/\s+$/, " ");
    if (newText.length <= CHARACTER_LIMIT) {
      setTextToUpload(newText);
    }
    if (newText?.length >= 1){
      window.isAnalysisDescFilled = true;
    } else{
      window.isAnalysisDescFilled = false;
    }
  };

  useEffect(() => {
    return () => {
        window.isAnalysisDescFilled = false;
    };
}, []);

useEffect(() => {
  const isFilled = textToUpload?.length >= 1 || review?.length >= 1;
	window.isAnalysisDescFilled = isFilled;
},[textToUpload, review]);

useEffect(() => {
  if(textToUpload){
    setTextToUpload('');
  }
}, [language]);

    // utils/validators.ts
const isValidYouTubeUrl = (url: string): boolean => {
  // Remove http:// or https:// at the start
  const cleanUrl = url.replace(/^https?:\/\//i, "").replace(/^www\./i, "").trim();

  // Match YouTube watch, shorts, or youtu.be formats
  const pattern = /^(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[\w-]{11}(?:[&?]\S*)?$/i;

  return pattern.test(cleanUrl);
};
 function clearData() {
    setTextToUpload("");
    setUrl("");
    setReview("");
    setError("");
    setFileType(null);
    window.isAnalysisDescFilled = false;
  }

/**
 * Check if a given URL is a YouTube or direct video URL.
 */
 function getVideoUrlType(url: string): "youtube" | "invalid" {

  if(!isValidYouTubeUrl(url)) return "invalid";
    if (!url) return "invalid";
    let parsedUrl: URL | null = null;
    try {
      const normalizedUrl = url.match(/^https?:\/\//i) ? url : `https://${url}`;
      parsedUrl = new URL(normalizedUrl.toLowerCase());
      // ✅ Check for YouTube URLs
        const isYouTube =
        parsedUrl.hostname.includes("youtube.com") ||
        parsedUrl.hostname.includes("youtu.be");
        if (isYouTube) return "youtube";
      return "invalid";
    } catch {
      return "invalid";
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {   
      let value = e.target.value;
      videoUrlRef.current = value;
      setError("");
      const typeOfUrl = getVideoUrlType(value);
      setVideoUrlType(typeOfUrl);
      value = value.replace(/^(https?:\/\/)?(www\.)?/i, "");
      setUrl(value.trim());
      if (!value) {
        setError("Please enter a valid YouTube URL");
      } else {
        if (typeOfUrl === "invalid"){
          setError("Please enter a valid YouTube URL");
        } else if (typeOfUrl === "youtube") {      
          setIsLoading(true);
          setError("");
          createAsset({url: `https://www.${value}`, name: "youtube_link" }).then((res) => {
            setAssetId(res.id);
          })
        }
    };
  }

  const _triggerAssetDescription = () => {
    let _rulesetValues = multiRuleset?.length ? multiRuleset?.[0]?.id: ruleSet?.id!;
    if (!assetId || !_rulesetValues || !language) return false;
    setIsLoading(true);
    setError("");
    triggerAssetDescription(
      assetId,
      _rulesetValues,
      language
    ).then((response) => {
      if (response.success) {
        setAsset(response?.asset);
        setTextToUpload(response?.asset?.description);
        setReview(response?.asset?.description);             
      }
      setIsLoading(false);
    }).catch((err) => {
      toast({
        variant: "warning",
        title: t("form_assetErrorMsg"),
      });
      clearData();
    }).finally(() => {
      setIsLoading(false);
      setError("");
    });
  }

  useEffect(() => {
    _triggerAssetDescription()
  }, [assetId, language, multiRuleset, ruleSet]);

  const cardStyles = css`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.white};
    padding: ${theme.spacing["4xl"]};
    box-shadow: ${theme.shadows.medium};
    border-radius: ${theme.radius.xs};
    margin-top: ${theme.spacing["5xl"]};
    margin-bottom: ${theme.spacing.xxl};
  `;

  const titleStyles = css`
    font-family: ${theme.typography.heading3_medium.fontFamily};
    font-size: ${theme.typography.heading3_medium.fontSize};
    font-weight: ${theme.typography.heading3_medium.fontWeight};
    color: ${theme.typography.heading3_medium.colors};
  `;

  const compulsoryStyles = css`
    font-family: ${theme.typography.subbody_medium.fontFamily};
    font-size: ${theme.typography.subbody_medium.fontSize};
    font-weight: ${theme.typography.subbody_medium.fontWeight};
    color: ${theme.typography.subbody_medium.colors};
  `;

  const radioItemStyles = css`
      fontFamily: ${theme.typography.bodyMedium.fontFamily};
      fontSize: ${theme.typography.bodyMedium.fontSize};
      fontWeight: ${theme.typography.body.fontWeight};
      padding: 10px;
      @media (max-width: 575px) {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 10px;
        div > div > label > div {
          height: auto;
        }
      }
  `;

  const formStyles = css`
    display: flex;
    flex-direction: column;
    margin-top: ${theme.spacing.xl};
    gap: ${theme.spacing.xl};
    font-family: ${theme.typography.bodyMedium.fontFamily};
    font-size: ${theme.typography.bodyMedium.fontSize};
    font-weight: ${theme.typography.bodyMedium.fontWeight};
    colors: ${theme.colors.purple};
  `;

  const inputRowStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing["4xl"]};
    > div {
      flex: 1;
      min-width: 200px;
    }
  `;

  const textareaStyles = css`
    color: ${theme.colors.textMedium};
    height: 172px;
    width: 100%;
    font-size: 16px;
    padding: 10px;
    box-sizing: border-box;
    resize: vertical;
    overflow: auto;
    border-radius: ${theme.radius.xs};
    border: 1px solid hsl(198, 11%, 71%);

    &:hover {
      background-color: var(--bg-secondary);
      border-color: var(--border-secondary);
    }
    &:focus {
      background-color: var(--bg-secondary);
      border-color: var(--border-brand-primary);
      outline: 2px solid transparent;
      outline-offset: 2px;
    }
  `;

  const counterStyles = css`
    font-size: ${theme.typography.bodyMedium.fontSize};
    color: ${textToUpload.length >= CHARACTER_LIMIT
      ? theme.colors.red
      : theme.colors.textMedium};
    text-align: right;
  `;


  const inputWrapper = css`
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 0px 0px;  
  background: #fff;
  margin: 5px 0px;
  input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 14px;
   padding: 0.75rem 0.25rem;    
    border-radius: 0.5rem;
  }
 &:focus-within {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primary};
    outline: 2px solid transparent;
    outline-offset: 2px;
    box-shadow: 0 0 0 2px #fff, 0 0 0 calc(2px + 2px) hsl(180, 98%, 25%), 0 0 #0000;
  }
  `


  const buttonContainerStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-top: ${theme.spacing.xl};

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      > button {
        align-self: flex-end;
      }
    }
  `;

useEffect(()=>{
  let _rulesetValues = multiRuleset?.length 
  ? multiRuleset.map((_item) => _item?.id).filter(Boolean).join(', ') 
  : ruleSet?.id!;
  analysesRenderRef.current = {
    name: campaign?.name,
    project_id: project?.id!,
    ruleset_id: _rulesetValues,
    asset_id: isUpload === 'text' ? undefined : asset?.id,
    config: { asset_description: isUpload === 'text'? textToUpload : review },
    notes: notes ?? "",
    lang: language,
    political_risk_ruleset_id: politicalRiskAssmnt,
  };
  setIsAnalyzing(false);
  const formValidCheck =
    !!(project && _rulesetValues && (campaign && campaign?.name?.trim()) && ((isUpload === 'text' || isUpload === 'url') ? textToUpload : review) && !isLoading);
  setIsFormVal(formValidCheck)
  setIsFormDirty(formValidCheck)
},[campaign,ruleSet, review, textToUpload, multiRuleset])

  useEffect(() => {
    if(firstRenderRef.current){
      firstRenderRef.current = false;
      return ;
    }
  }, [isFormVal, newAnalysisRef.current]);


  useEffect(() => {
    clearData();
  }, [isUpload]);

  const launchAnalysis = () => {
    setIsFormDirty(false);
    if (isAnalyzing) return;
    setIsAnalyzing(true);
    newAnalysisRef.current = true;

    let _rulesetValues = multiRuleset?.length 
    ? multiRuleset.map((_item) => _item?.id).filter(Boolean).join(', ') 
    : ruleSet?.id!;

    const analysis = {
      name: campaign!.name,
      project_id: project!.id!,
      ruleset_id: _rulesetValues,
      asset_id: isUpload === 'text' ? undefined : asset?.id,
      config: { asset_description: isUpload === 'text'? textToUpload : review },
      notes: notes ?? "",
      lang: language,
      political_risk_ruleset_id: politicalRiskAssmnt,
    };
    createAnalysis(analysis)
      .then((newAnalysis) => {
        const newAnalysisId = newAnalysis.id;
        setIsAnalyzing(false);
        executeAnalysis(newAnalysis.id, language);
        navigate(`/analyses/results?analysis=${newAnalysisId}`);
      })
      .catch((error) => {
        console.error(error);
      });
  }
  const draftAnalysis = () => {
    newAnalysisRef.current = true;

    let _rulesetValues = multiRuleset?.length 
    ? multiRuleset.map((_item) => _item?.id).filter(Boolean).join(', ') 
    : ruleSet?.id!;
    
    const analysis = {
      name: campaign!.name,
      project_id: project!.id!,
      ruleset_id: _rulesetValues,
      asset_id: isUpload === 'text' ? undefined : asset?.id,
      config: { asset_description: isUpload === 'text' ? textToUpload : review },
      notes: notes ?? "",
      lang: language,
      status: "draft",
      political_risk_ruleset_id: politicalRiskAssmnt,
    };

    createAnalysis(analysis)
      .then(() => {
        setReRenderAnalysisList(true);
        toast({
          variant: "success",
          title: t("analysis_toast_add_success"),
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsAnalyzing(false);
      });
  };

  const isFormValid =
    project &&
    (ruleSet || multiRuleset?.length) &&
    campaign &&
    ((isUpload === 'text' || isUpload === 'url') ? textToUpload : review) &&
    !isLoading;

    if(isAnalyzing){
       window.scrollTo(0, 0)
       return <PageLoader />
    } 
  return (
    <div css={cardStyles}>
      <div css={titleStyles}> {t("analyse_form_title")} </div>
      <form css={formStyles}>
        <div css={inputRowStyles}>
          <ProjectTextField setProject={setProject} projectId={null} />
          <TextFieldWithRadioDropdown 
            setRulesets={setRuleSet} 
            setMultiRulesets={setMultiRuleset}
            setPoliticalRiskAssmnt={setPoliticalRiskAssmnt}
            ruleId={null} 
            fileType={fileType}
            assetType={isUpload}
          />
        </div>
        <div css={inputRowStyles}>
          <CampaignTextField setCampaign={setCampaign} campaignName={null} project={project?.id || null} />
          <Input
            name="note" 
            label={t("analyse_form_label_note")}
            onChange={(ev) => setNotes(ev.target.value)}
            autoComplete="off"
          />
        </div>
        <RadioGroup
          name="uploadMethod"
          defaultValue="file"
          orientation="horizontal"
          className="radio-group"
          css={radioItemStyles}
        >
          <RadioGroupItem
            id="r1"
            label={t("form_inputFileImageDescription")}
            value="file"
            onClick={() => setIsUpload('file')}
          />
          <RadioGroupItem
            id="r2"
            label={t("form_inputFileTexteDescription")}
            value="description"
            onClick={() => setIsUpload('text')}
          />

          <RadioGroupItem
            id="r3"
            label={t("form_inputVideoUrlDescription")}
            value="url"
            onClick={() =>{
              setIsUpload('url');
              setIsLoading(false);}
            } 
          />
          

        </RadioGroup>
        {isUpload==='file' && (
          <FileInput
            assetId={asset?.id}
            setFileType={setFileType}            
            setAsset={setAsset}
            rulesetId={ruleSet?.id ?? multiRuleset?.[0]?.id ?? null}
            setIsLoading={setIsLoading}
            setReview={setReview}
            review={review}
            isLoading={isLoading}
            politicalRiskAssmnt={politicalRiskAssmnt}
          />
        ) 
      }
        {isUpload==='text' && ( 
        <div
          css={css`
            gap: 0;
          `}
        >
          <div css={counterStyles}>
            {textToUpload.length}/{CHARACTER_LIMIT}
          </div>
          <textarea
            placeholder={t("form_inputTextareaPlaceholder")}
            name="description"
            css={textareaStyles}
            value={textToUpload}
            onChange={handleTextChange}
          />
        </div>
      )}
      {isUpload === "url" && (
          <YoutubeUrlUpload
            isLoading={isLoading}
            url={url}
            setUrl={setUrl}
            textToUpload={textToUpload}
            videoUrlType={videoUrlType}
            error={error}
            clearData={clearData}
            handleUrlChange={handleUrlChange}
            handleTextChange={setTextToUpload}
            CHARACTER_LIMIT={CHARACTER_LIMIT}
            t={t}
            inputWrapper={inputWrapper}
            videoUrlRef={videoUrlRef}
            asset={asset}
          />
        )
      }    
        <div css={buttonContainerStyles}>
          <div css={compulsoryStyles}>{t("core_compulsory_field")}</div>          
          <Button
            name="analyzeButton"
            variant={"brand"}
            icon={"send"}
            color={theme.colors.primary}
            onClick={() => launchAnalysis()}
            disabled={!isFormValid || isAnalyzing}
            className="brandPrimaryBtn"
          >
            {t("form_inputButton")}
          </Button>
        </div>
      </form>
      <NavigationBlocker
        shouldBlock={isFormDirty}
        onClose={() => {}}
        dialogTitle={t("analysis_draft_title")}
        dialogMessage={t("analysis_draft_message")}
        onCancel={() => {}}
        cancelText={t("analysis_draft_cancel")}
        onProceed={
          () => {
            draftAnalysis();
          }
        }
        proceedText={t("analysis_draft_save")}
        onDiscard={() => {}}
        descrdText={t("analysis_draft_no_delete")}
      />
    </div>
  );
}
